/**
 * Admin Sync Health Dashboard
 * FEAT-SYNC-004: Monitor Clerk ↔ D1 synchronization
 */

import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useAuth } from '@clerk/clerk-react';
import {
  Activity,
  AlertCircle,
  CheckCircle,
  Clock,
  Database,
  Loader2,
  RefreshCw,
  Users,
  Wrench,
  XCircle,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';

const API_URL = import.meta.env.VITE_API_URL || 'https://erg-shoot.des-466.workers.dev/api';

interface SyncHealthMetrics {
  users_in_d1: number;
  users_with_clerk_id: number;
  orphan_users: number;
  pending_invitations: number;
  failed_invitations: number;
  needs_manual_invitations: number;
  sync_events_24h: number;
  failed_syncs_24h: number;
  last_webhook_received: string | null;
}

interface OrphanUser {
  id: string;
  email: string;
  name: string | null;
  role: string;
  is_active: number;
  created_at: string;
  invitation_status: string | null;
}

interface SyncLog {
  id: string;
  event_type: string;
  user_id: string | null;
  clerk_user_id: string | null;
  source: string;
  status: string;
  details: string | null;
  error_message: string | null;
  processed_at: string | null;
  created_at: string;
}

export default function AdminSyncHealth() {
  const { getToken } = useAuth();
  const [metrics, setMetrics] = useState<SyncHealthMetrics | null>(null);
  const [orphans, setOrphans] = useState<OrphanUser[]>([]);
  const [logs, setLogs] = useState<SyncLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [repairingId, setRepairingId] = useState<string | null>(null);
  const [retryingInvitations, setRetryingInvitations] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const token = await getToken();
      const headers = { Authorization: `Bearer ${token}` };

      const [metricsRes, orphansRes, logsRes] = await Promise.all([
        fetch(`${API_URL}/admin/sync/health`, { headers }),
        fetch(`${API_URL}/admin/sync/orphans`, { headers }),
        fetch(`${API_URL}/admin/sync/logs?limit=20`, { headers }),
      ]);

      if (metricsRes.ok) {
        const data = await metricsRes.json();
        setMetrics(data.data);
      }

      if (orphansRes.ok) {
        const data = await orphansRes.json();
        setOrphans(data.data?.orphans || []);
      }

      if (logsRes.ok) {
        const data = await logsRes.json();
        setLogs(data.data?.logs || []);
      }
    } catch (error) {
      console.error('Error fetching sync data:', error);
      toast.error('Failed to fetch sync health data');
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRepair = async (userId: string) => {
    setRepairingId(userId);
    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/admin/sync/repair/${userId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('User sync repaired successfully');
        fetchData();
      } else {
        toast.error(data.error || 'Failed to repair user sync');
      }
    } catch (error) {
      console.error('Error repairing user:', error);
      toast.error('Failed to repair user sync');
    } finally {
      setRepairingId(null);
    }
  };

  const handleRetryInvitations = async () => {
    setRetryingInvitations(true);
    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/admin/sync/retry-invitations`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(`Reset ${data.data?.affected || 0} invitations for retry`);
        fetchData();
      } else {
        toast.error(data.error || 'Failed to retry invitations');
      }
    } catch (error) {
      console.error('Error retrying invitations:', error);
      toast.error('Failed to retry invitations');
    } finally {
      setRetryingInvitations(false);
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Sync Health Dashboard</h1>
            <p className="text-muted-foreground">Monitor Clerk ↔ D1 synchronization status</p>
          </div>
          <Button onClick={fetchData} variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Health Metrics */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">D1 Users</CardTitle>
              <Database className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{metrics?.users_in_d1 || 0}</div>
              <p className="text-xs text-muted-foreground">
                {metrics?.users_with_clerk_id || 0} with Clerk ID
              </p>
            </CardContent>
          </Card>

          <Card className={metrics?.orphan_users ? 'border-yellow-500/50' : ''}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Orphan Users</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{metrics?.orphan_users || 0}</div>
              <p className="text-xs text-muted-foreground">Need sync repair</p>
            </CardContent>
          </Card>

          <Card className={metrics?.failed_syncs_24h ? 'border-destructive/50' : ''}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Sync Events (24h)</CardTitle>
              <Activity className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{metrics?.sync_events_24h || 0}</div>
              <p className="text-xs text-muted-foreground">
                {metrics?.failed_syncs_24h || 0} failed
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Last Webhook</CardTitle>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-sm font-medium">
                {formatDate(metrics?.last_webhook_received || null)}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Invitation Status */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Invitation Status</CardTitle>
              <CardDescription>Track Clerk invitation delivery</CardDescription>
            </div>
            {(metrics?.failed_invitations || 0) + (metrics?.needs_manual_invitations || 0) > 0 && (
              <Button
                onClick={handleRetryInvitations}
                disabled={retryingInvitations}
                variant="outline"
                size="sm"
              >
                {retryingInvitations ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Retry Failed
                  </>
                )}
              </Button>
            )}
          </CardHeader>
          <CardContent>
            <div className="flex gap-4">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-yellow-500" />
                <span className="text-sm">{metrics?.pending_invitations || 0} Pending</span>
              </div>
              <div className="flex items-center gap-2">
                <XCircle className="h-4 w-4 text-destructive" />
                <span className="text-sm">{metrics?.failed_invitations || 0} Failed</span>
              </div>
              <div className="flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-orange-500" />
                <span className="text-sm">
                  {metrics?.needs_manual_invitations || 0} Needs Manual
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Orphan Users */}
        {orphans.length > 0 && (
          <Card className="border-yellow-500/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-yellow-500" />
                Orphan Users (Need Sync Repair)
              </CardTitle>
              <CardDescription>
                These users exist in D1 but don't have a proper Clerk ID link
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Invitation Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {orphans.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell className="font-medium">{user.email}</TableCell>
                      <TableCell>{user.name || '-'}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{user.role}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            user.invitation_status === 'accepted'
                              ? 'default'
                              : user.invitation_status === 'failed'
                                ? 'destructive'
                                : 'secondary'
                          }
                        >
                          {user.invitation_status || 'none'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleRepair(user.id)}
                          disabled={repairingId === user.id}
                        >
                          {repairingId === user.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <>
                              <Wrench className="h-4 w-4 mr-1" />
                              Repair
                            </>
                          )}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Recent Sync Logs */}
        <Card>
          <CardHeader>
            <CardTitle>Recent Sync Events</CardTitle>
            <CardDescription>Last 20 synchronization events</CardDescription>
          </CardHeader>
          <CardContent>
            {logs.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Activity className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No sync events recorded yet</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Event</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Time</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell>
                        <div className="font-medium">{log.event_type}</div>
                        {log.user_id && (
                          <div className="text-xs text-muted-foreground truncate max-w-[200px]">
                            {log.user_id}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{log.source}</Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {log.status === 'success' && (
                            <CheckCircle className="h-4 w-4 text-green-500" />
                          )}
                          {log.status === 'failed' && (
                            <XCircle className="h-4 w-4 text-destructive" />
                          )}
                          {log.status === 'pending' && (
                            <Clock className="h-4 w-4 text-yellow-500" />
                          )}
                          <span className="text-sm">{log.status}</span>
                        </div>
                        {log.error_message && (
                          <div className="text-xs text-destructive truncate max-w-[200px]">
                            {log.error_message}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">{formatDate(log.created_at)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
